/* eslint-disable react-hooks/set-state-in-effect */
/**
 * RegionMap V2 — atlas de voyageur richement illustré.
 * Terrain biomes colorés, mer avec houle, rose des vents, cartouche vintage,
 * nœuds stylisés par biome, sprite dresseur pulsant, support "tous dresseurs".
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Lock, MountainSnow, Castle, Anchor, TowerControl, Trees, Landmark,
  Waves, TentTree, Zap, Sparkles, Route as RouteIcon, Flag,
} from "lucide-react";

const spriteUrl = (spriteId) =>
  `https://play.pokemonshowdown.com/sprites/trainers/${spriteId || "youngster"}.png`;

const KIND_ICON = {
  route: RouteIcon, forest: Trees, cave: MountainSnow, tower: TowerControl,
  city: Landmark, port: Anchor, lab: Castle, safari: TentTree, lake: Waves,
  power: Zap, lighthouse: TowerControl, mountain: Sparkles,
};

const KIND_STYLE = {
  route:      { fill: "#F1E4C4", stroke: "#7A5A2E", accent: "#7A5A2E" },
  forest:     { fill: "#94B87A", stroke: "#2F5A2A", accent: "#1E3D18" },
  cave:       { fill: "#7A6F5C", stroke: "#3B2E1D", accent: "#F2E8D5" },
  tower:      { fill: "#B8A9E0", stroke: "#4B3A70", accent: "#2E1E52" },
  city:       { fill: "#E86D3F", stroke: "#7A2E12", accent: "#FFF4D9" },
  port:       { fill: "#4E8BC4", stroke: "#22447A", accent: "#FCF9F2" },
  lab:        { fill: "#D8B4E0", stroke: "#5A3860", accent: "#2E1E52" },
  safari:     { fill: "#E0C568", stroke: "#7A5A18", accent: "#3B2E1D" },
  lake:       { fill: "#8DC5E0", stroke: "#2E5A78", accent: "#1E3D52" },
  power:      { fill: "#F6C747", stroke: "#7A5A18", accent: "#3B2E1D" },
  lighthouse: { fill: "#F2E8D5", stroke: "#7A5A2E", accent: "#7A2E12" },
  mountain:   { fill: "#B0A48B", stroke: "#4B3A2A", accent: "#F2E8D5" },
};

const REGION_ACCENT = {
  kanto: { banner: "#E3350D", tint: "#F5EAD1", sea: "#B8DBE8" },
  johto: { banner: "#3165B5", tint: "#EDE9D0", sea: "#A9CDDE" },
  hoenn: { banner: "#20A34D", tint: "#F1EAC8", sea: "#A0D0E4" },
};

const stateStyles = (accent) => ({
  current:    { size: 32, ring: 4, halo: `0 0 0 6px ${accent.banner}33, 0 0 24px 6px ${accent.banner}99` },
  neighbor:   { size: 26, ring: 3, halo: `0 0 0 3px #F6C74755, 0 0 14px 2px #F6C74799` },
  accessible: { size: 22, ring: 2, halo: "none" },
  locked:     { size: 20, ring: 1, halo: "none", opacity: 0.42 },
});

export default function RegionMap({ trainerId, regions = [], onTravelled, extraTrainers = [], showAllTrainers = false }) {
  const activeRegions = useMemo(() => regions.filter((r) => r.unlocked), [regions]);
  const [regionId, setRegionId] = useState(activeRegions[0]?.id || "kanto");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [hoverTrainer, setHoverTrainer] = useState(null);

  const load = useCallback(async () => {
    if (!trainerId || !regionId) return;
    try {
      const { data } = await api.get(`/trainers/${trainerId}/map/${regionId}`);
      setData(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, [trainerId, regionId]);
  useEffect(() => { load(); }, [load]);

  const travel = async (loc) => {
    if (!data?.trainer?.is_active_region) return;
    setBusy(true);
    try {
      await api.post(`/trainers/${trainerId}/travel`, { location_id: loc.id });
      toast.success(`Cap sur ${loc.label} 🚶`);
      setSelectedId(null);
      await load();
      onTravelled?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const travelTo = async (loc) => {
    if (!data?.trainer?.is_active_region) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/trainers/${trainerId}/travel_to`, { location_id: loc.id });
      const steps = res.steps ?? (res.path?.length || 0);
      toast.success(`🗺️ Déplacement vers ${loc.label} — ${steps} étape(s)`);
      setSelectedId(null);
      await load();
      onTravelled?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="font-mono text-xs text-[#7A7A7A]">Relevé topographique en cours…</div>;

  const accent = REGION_ACCENT[regionId] || REGION_ACCENT.kanto;
  const { nodes, edges, gyms, trainer } = data;
  const nodesById = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const st = stateStyles(accent);
  const canSwitch = activeRegions.length > 1;

  const otherTrainersHere = showAllTrainers
    ? extraTrainers.filter((t) => t.region === regionId && t.id !== trainer.id && t.location)
    : [];

  return (
    <div data-testid="region-map" className="relative select-none">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="overline text-[10px]" style={{ color: accent.banner }}>Carte relevée par le Prof.</div>
          <h3 className="font-serif-display text-3xl italic leading-none tracking-tight">{regionId.toUpperCase()}</h3>
          <div className="font-mono text-[10px] text-[#4A4A4A] mt-1">
            {trainer.is_active_region ? (
              <>
                <span className="inline-block w-2 h-2 mr-1 border border-[#1E2D24]" style={{ background: accent.banner }}/>
                {trainer.name} · <strong>{nodesById[trainer.current_location]?.label || trainer.current_location}</strong>
                {otherTrainersHere.length > 0 && <> · <em>+ {otherTrainersHere.length} autre(s) ici</em></>}
              </>
            ) : (
              <span className="italic text-[#8C877A]">{trainer.name} est en {trainer.current_region.toUpperCase()}.</span>
            )}
          </div>
        </div>
        {canSwitch && (
          <div className="flex" data-testid="region-map-switcher">
            {activeRegions.map((r, idx) => (
              <button key={r.id} data-testid={`region-map-switch-${r.id}`}
                onClick={() => { setRegionId(r.id); setSelectedId(null); }}
                className={`px-3 py-1 font-mono text-[10px] uppercase tracking-widest border-2 border-[#1E2D24] ${idx > 0 ? "-ml-[2px]" : ""} ${r.id === regionId ? "text-[#F2E8D5]" : "bg-[#F2E8D5] text-[#1E2D24] hover:bg-[#EAE4D2]"}`}
                style={r.id === regionId ? { background: (REGION_ACCENT[r.id] || accent).banner } : {}}>
                {r.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div
        className="relative border-[3px] border-[#1E2D24] w-full overflow-hidden"
        style={{
          aspectRatio: "3 / 2",
          background: `
            radial-gradient(ellipse at 12% 88%, ${accent.sea}bb 0%, transparent 42%),
            radial-gradient(ellipse at 88% 82%, ${accent.sea}bb 0%, transparent 38%),
            radial-gradient(circle at 30% 50%, #d5c194 0%, transparent 55%),
            radial-gradient(circle at 70% 40%, #d5c194 0%, transparent 50%),
            repeating-linear-gradient(45deg, transparent 0 2px, rgba(50,30,10,0.04) 2px 3px),
            ${accent.tint}
          `,
          boxShadow: "6px 6px 0px #1E2D24",
        }}
        data-testid="region-map-canvas"
      >
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="waves" x="0" y="0" width="4" height="2" patternUnits="userSpaceOnUse">
              <path d="M0 1 Q1 0 2 1 T4 1" fill="none" stroke={accent.sea} strokeWidth="0.15" opacity="0.7"/>
            </pattern>
            <filter id="ink-blur"><feGaussianBlur stdDeviation="0.15"/></filter>
          </defs>
          <rect x="0" y="80" width="100" height="20" fill="url(#waves)"/>
          <rect x="0" y="0"  width="14" height="100" fill="url(#waves)" opacity="0.5"/>
          <rect x="86" y="0" width="14" height="100" fill="url(#waves)" opacity="0.5"/>
        </svg>

        <div className="absolute top-2 left-2 px-3 py-1 font-serif-display text-sm italic tracking-wider border-2 border-[#1E2D24]"
          style={{ background: accent.banner, color: "#F2E8D5", boxShadow: "2px 2px 0 #1E2D24" }}>
          ✦ {regionId.charAt(0).toUpperCase() + regionId.slice(1)} ✦
        </div>

        <div className="absolute top-2 right-2 w-14 h-14 opacity-75" title="Nord ↑">
          <svg viewBox="0 0 40 40" className="w-full h-full">
            <g stroke="#3B2E1D" strokeWidth="0.8" fill="none">
              <circle cx="20" cy="20" r="14" strokeDasharray="1 1"/>
              <path d="M20 4 L22 20 L20 36 L18 20 Z" fill="#7A2E12" stroke="#3B2E1D"/>
              <path d="M4 20 L20 22 L36 20 L20 18 Z" fill="#7A5A2E" stroke="#3B2E1D"/>
            </g>
            <text x="20" y="10" textAnchor="middle" fontSize="6" fontFamily="serif" fill="#3B2E1D" fontWeight="bold">N</text>
          </svg>
        </div>

        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
          {edges.map((e, i) => {
            const a = nodesById[e.from], b = nodesById[e.to];
            if (!a || !b) return null;
            const isHot = trainer.is_active_region && (a.id === trainer.current_location || b.id === trainer.current_location) && !a.locked && !b.locked;
            const bothLocked = a.locked && b.locked;
            const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
            const dx = b.x - a.x, dy = b.y - a.y;
            const len = Math.max(1, Math.hypot(dx, dy));
            const cx = mx - dy / len * 1.6, cy = my + dx / len * 1.6;
            return (
              <path key={i} d={`M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}`} fill="none"
                stroke={isHot ? accent.banner : "#3B2E1D"} strokeWidth={isHot ? 0.55 : 0.32}
                strokeDasharray={isHot ? "" : "0.9 0.7"} strokeLinecap="round"
                opacity={bothLocked ? 0.3 : (isHot ? 1 : 0.7)} vectorEffect="non-scaling-stroke"/>
            );
          })}
        </svg>

        {nodes.map((n) => {
          const Icon = KIND_ICON[n.kind] || RouteIcon;
          const ks = KIND_STYLE[n.kind] || KIND_STYLE.route;
          const state = n.current ? "current" : n.locked ? "locked" : n.reachable ? "neighbor" : "accessible";
          const s = st[state];
          return (
            <button key={n.id} data-testid={`map-node-${n.id}`} disabled={busy}
              onClick={() => setSelectedId(n.id)}
              title={n.label + (n.locked ? ` (verrouillé — ${n.min_badges} badges)` : "")}
              className={`absolute flex items-center justify-center cursor-pointer transition-transform hover:scale-110 ${state === "current" ? "animate-[pulse_2.5s_ease-in-out_infinite]" : ""}`}
              style={{
                left: `${n.x}%`, top: `${n.y}%`, width: s.size, height: s.size,
                transform: "translate(-50%,-50%)",
                borderRadius: n.kind === "city" || n.kind === "lab" ? 0 : "50%",
                background: n.locked ? "#B0A48B" : ks.fill,
                border: `${s.ring}px solid ${n.locked ? "#5A5A50" : ks.stroke}`,
                color: n.locked ? "#3B2E1D" : ks.accent,
                opacity: s.opacity ?? 1, boxShadow: s.halo,
                zIndex: state === "current" ? 20 : state === "neighbor" ? 15 : 10,
              }}>
              {n.mastered && !n.locked && !n.current && (
                <span className="absolute -top-3 -right-2 font-serif-display italic text-[9px] leading-none px-1 py-[1px] rotate-[-10deg] pointer-events-none border"
                  data-testid={`map-mastered-${n.id}`}
                  style={{ background: "#F2E8D5", color: accent.banner, borderColor: accent.banner, boxShadow: "1px 1px 0 rgba(0,0,0,0.2)" }}>VU</span>
              )}
              {n.current ? (
                <img src={spriteUrl(trainer.sprite_id)} alt={trainer.name} className="pixelated w-7 h-7"
                  onError={(e) => { e.target.style.display = "none"; }}/>
              ) : n.locked ? <Lock size={11}/> : <Icon size={11} strokeWidth={2.4}/>}
            </button>
          );
        })}

        {gyms.map((g) => (
          <div key={g.id} data-testid={`map-gym-${g.id}`}
            title={`Arène ${g.leader} · ${g.type}${g.beaten ? " (vaincu)" : ""}`}
            className="absolute pointer-events-none"
            style={{ left: `${g.x}%`, top: `${g.y}%`, transform: "translate(-50%,-50%)", zIndex: 12 }}>
            <svg width="18" height="18" viewBox="0 0 20 20">
              <polygon points="10,1 19,7 16,18 4,18 1,7"
                fill={g.beaten ? "#F6C747" : "#F2E8D5"}
                stroke={g.beaten ? "#7A5A18" : "#3B2E1D"} strokeWidth="1.5"/>
              <text x="10" y="13" textAnchor="middle" fontSize="7" fontFamily="serif" fontWeight="bold"
                fill={g.beaten ? "#3B2E1D" : "#7A7062"}>{g.beaten ? "★" : "?"}</text>
            </svg>
          </div>
        ))}

        {otherTrainersHere.map((t, idx) => {
          const node = nodesById[t.location];
          if (!node) return null;
          const offset = ((idx % 4) - 1.5) * 10;
          return (
            <div key={t.id} data-testid={`map-trainer-${t.id}`}
              className="absolute cursor-pointer transition-transform hover:scale-125"
              style={{
                left: `${node.x}%`, top: `${node.y}%`,
                transform: `translate(calc(-50% + ${offset}px), calc(-50% - 26px))`,
                zIndex: 18,
              }}
              onMouseEnter={() => setHoverTrainer(t.id)}
              onMouseLeave={() => setHoverTrainer((h) => (h === t.id ? null : h))}
              onClick={(e) => { e.stopPropagation(); t.onClick?.(); }}>
              <div className="w-7 h-7 border-2 border-[#1E2D24] flex items-center justify-center"
                style={{ background: "#FCF9F2", borderRadius: "50%", boxShadow: "1px 1px 0 rgba(0,0,0,0.3)" }}>
                <img src={spriteUrl(t.sprite_id)} alt={t.name} className="pixelated w-5 h-5"
                  onError={(e) => { e.target.style.display = "none"; }}/>
              </div>
              {hoverTrainer === t.id && (
                <div className="absolute left-1/2 -translate-x-1/2 -top-6 px-2 py-[2px] font-mono text-[8px] whitespace-nowrap border border-[#1E2D24] bg-[#FCF9F2]"
                  style={{ boxShadow: "1px 1px 0 #1E2D24" }}>
                  {t.name}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-2 border-2 border-[#1E2D24] bg-[#F2E8D5] p-2 font-mono text-[9px] uppercase tracking-wider"
        style={{ boxShadow: "3px 3px 0px #1E2D24" }} data-testid="region-map-legend">
        <LegendItem color={accent.banner} label="Position actuelle"/>
        <LegendItem color="#F6C747" label="Atteignable" outline/>
        <LegendItem color="#F2E8D5" label="Accessible" outline dark/>
        <LegendItem color="#B0A48B" label="Verrouillé"/>
        <div className="flex items-center gap-1">
          <svg width="14" height="14" viewBox="0 0 20 20"><polygon points="10,1 19,7 16,18 4,18 1,7" fill="#F6C747" stroke="#7A5A18" strokeWidth="1.5"/></svg>
          Badge acquis
        </div>
        <div className="flex items-center gap-1">
          <span className="font-serif-display italic text-[10px]" style={{ color: accent.banner }}>VU</span>
          Route maîtrisée
        </div>
      </div>

      <p className="text-[10px] text-[#8C877A] italic mt-2 font-mono">
        Clique une route pour voir les Pokémon aperçus. Route limitrophe → « Voyager ici ». Route éloignée → « Déplacement » (trajet automatique route par route).
      </p>

      {selectedId && nodesById[selectedId] && (
        <NodeDetailPanel node={nodesById[selectedId]} accent={accent}
          canTravel={data.trainer.is_active_region && nodesById[selectedId].reachable && !busy}
          canTravelTo={
            data.trainer.is_active_region &&
            !nodesById[selectedId].locked &&
            !nodesById[selectedId].current &&
            !nodesById[selectedId].reachable &&
            !busy
          }
          onTravel={() => travel(nodesById[selectedId])}
          onTravelTo={() => travelTo(nodesById[selectedId])}
          onClose={() => setSelectedId(null)}/>
      )}

      {data.trainer.is_active_region && data.trainer.travel_queue_len > 0 && data.trainer.travel_destination && (
        <div className="mt-3 border-2 border-[#1E2D24] p-2 flex items-center gap-2 font-mono text-[11px]"
          style={{ background: "#F6C74722", boxShadow: "2px 2px 0 #1E2D24" }}
          data-testid="travel-status-banner">
          <Flag size={14} className="text-[#7A5A18]"/>
          <span className="flex-1">
            Déplacement en cours · destination <strong>{nodesById[data.trainer.travel_destination]?.label || data.trainer.travel_destination}</strong>
            {" · "}<em>{data.trainer.travel_queue_len} étape(s) restante(s)</em>
          </span>
        </div>
      )}
    </div>
  );
}

function LegendItem({ color, label, outline = false, dark = false }) {
  return (
    <div className="flex items-center gap-1">
      <span className="inline-block w-3 h-3 border-2 rounded-full"
        style={{ background: outline ? "transparent" : color, borderColor: outline ? color : "#1E2D24", color: dark ? "#1E2D24" : undefined }}/>
      {label}
    </div>
  );
}

function NodeDetailPanel({ node, accent, canTravel, canTravelTo, onTravel, onTravelTo, onClose }) {
  const previews = node.pokemon_preview || [];
  const total = node.pokemon_total ?? previews.length;
  return (
    <div className="mt-3 border-[3px] border-[#1E2D24] bg-[#FCF9F2]"
      style={{ boxShadow: "4px 4px 0px #1E2D24" }} data-testid="map-node-detail">
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-[#1E2D24]"
        style={{ background: node.mastered ? "#F2E8D5" : "#F5EAD1" }}>
        <div>
          <div className="overline text-[9px]" style={{ color: accent.banner }}>
            {node.locked ? "Route verrouillée" : node.current ? "Position actuelle"
              : node.reachable ? "Route limitrophe" : "Route accessible"}
            {node.mastered && !node.locked && (
              <span className="ml-2 font-serif-display italic text-[10px]" style={{ color: accent.banner }}>VU</span>
            )}
          </div>
          <h4 className="font-serif-display text-lg italic leading-tight" data-testid="map-node-detail-title">{node.label}</h4>
          <div className="font-mono text-[10px] text-[#4A4A4A]">
            Difficulté {node.difficulty} · {node.min_badges} badge(s) · {total} espèces
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {canTravel && (
            <button className="btn-ink btn-leaf text-[10px] py-1 px-2" onClick={onTravel} data-testid="map-node-detail-travel">
              <Flag size={10}/> Voyager ici
            </button>
          )}
          {canTravelTo && (
            <button className="btn-ink text-[10px] py-1 px-2" onClick={onTravelTo} data-testid="map-node-detail-travelto"
              style={{ background: "#F6C747", borderColor: "#7A5A18" }}
              title="Déplacement route par route jusqu'à cette destination">
              <Flag size={10}/> Déplacement
            </button>
          )}
          <button className="btn-ink text-[10px] py-1 px-2" onClick={onClose} data-testid="map-node-detail-close">Fermer</button>
        </div>
      </div>
      <div className="p-3">
        <div className="overline text-[9px] mb-2">Pokémon aperçus ({previews.length}/{total})</div>
        {previews.length === 0 ? (
          <p className="font-mono text-[11px] text-[#8C877A] italic">
            Aucun Pokémon aperçu — envoie un dresseur explorer pour lever le brouillard.
          </p>
        ) : (
          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-1">
            {previews.map((pid) => (
              <div key={pid} className="border border-[#1E2D24] bg-[#F2E8D5] p-1 text-center"
                data-testid={`map-node-preview-${pid}`} title={`#${String(pid).padStart(3, "0")}`}>
                <img src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pid}.png`}
                  alt={`#${pid}`} className="pixelated w-10 h-10 mx-auto" loading="lazy"/>
                <div className="font-mono text-[8px] text-[#4A4A4A]">#{String(pid).padStart(3, "0")}</div>
              </div>
            ))}
          </div>
        )}
        {previews.length < total && (
          <p className="font-mono text-[9px] text-[#8C877A] italic mt-2">
            {total - previews.length} espèce(s) inconnue(s) — le prof lèvera le voile au fil des explorations.
          </p>
        )}
      </div>
    </div>
  );
}
